import { useSessionUser } from "hooks/useSession";
import { useCallback, useEffect, useState } from "react";

import { setPosthogSupportIdentity } from "config/posthog-runtime";

import { closePosthogConversations, openPosthogConversations } from "./openPosthogConversations";
import {
  isPosthogConversationsPanelOpen,
  observePosthogConversationsPanelOpen,
} from "./posthogConversationsDom";
import { waitForPosthogConversations } from "./waitForPosthogConversations";

const COOKIE_CONSENT_EVENT = "rivetai:cookie-consent";

const readConversationsAvailability = async (): Promise<boolean> => {
  const conversations = await waitForPosthogConversations(15_000);
  return conversations !== null;
};

export const usePosthogSupport = () => {
  const { session: profile } = useSessionUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  const ensureSupportIdentity = useCallback(async (): Promise<boolean> => {
    if (!profile?.user_id || !profile.posthog_identity_hash) {
      return false;
    }

    return setPosthogSupportIdentity(profile.user_id, profile.posthog_identity_hash);
  }, [profile?.posthog_identity_hash, profile?.user_id]);

  useEffect(() => {
    let mounted = true;

    const syncAvailability = async () => {
      const available = await readConversationsAvailability();
      if (!mounted) {
        return;
      }

      setIsAvailable(available);

      if (available) {
        setIsOpen(isPosthogConversationsPanelOpen());
      }
    };

    void syncAvailability();

    const onConsentChange = () => {
      void syncAvailability();
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, onConsentChange);

    return () => {
      mounted = false;
      window.removeEventListener(COOKIE_CONSENT_EVENT, onConsentChange);
    };
  }, []);

  useEffect(() => observePosthogConversationsPanelOpen(setIsOpen), []);

  const hide = useCallback(async () => {
    const conversations = await waitForPosthogConversations();
    if (!conversations) {
      return false;
    }
    closePosthogConversations(conversations);
    setIsOpen(false);
    setIsAvailable(true);
    return true;
  }, []);

  const show = useCallback(async () => {
    const conversations = await waitForPosthogConversations();
    if (!conversations) {
      return false;
    }

    await ensureSupportIdentity();
    const opened = await openPosthogConversations(conversations);
    setIsOpen(opened);
    setIsAvailable(true);
    return opened;
  }, [ensureSupportIdentity]);

  const toggle = useCallback(async () => {
    const conversations = await waitForPosthogConversations();
    if (!conversations) {
      return false;
    }
    if (isPosthogConversationsPanelOpen()) {
      closePosthogConversations(conversations);
      setIsOpen(false);
    } else {
      await ensureSupportIdentity();
      const opened = await openPosthogConversations(conversations);
      setIsOpen(opened);
      if (!opened) {
        return false;
      }
    }
    setIsAvailable(true);
    return true;
  }, [ensureSupportIdentity]);

  return {
    hide,
    isAvailable,
    isOpen,
    show,
    toggle,
  };
};
